import React, { ChangeEvent, KeyboardEvent, useEffect, useState } from 'react';

import {
  Button,
  Icon,
  Input,
  Stack,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@eluve/components';

export interface InlineEditProps {
  value: string;
  onSave: (newValue: string) => void;
  onCancel?: () => void;
  isLoading?: boolean;
  isReadOnly?: boolean;
  multiline?: boolean;
  placeholder?: string;
  className?: string;
  maxLength?: number;
  children: React.ReactNode;
}

export const InlineEdit: React.FC<InlineEditProps> = ({
  value,
  onSave,
  onCancel,
  isLoading = false,
  isReadOnly = false,
  multiline = false,
  placeholder,
  className,
  maxLength,
  children,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(value);

  useEffect(() => {
    setEditValue(value);
  }, [value]);

  const handleSave = () => {
    if (editValue !== value) {
      onSave(editValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(value);
    setIsEditing(false);
    onCancel?.();
  };

  if (isEditing) {
    const InputComponent = multiline ? Textarea : Input;

    return (
      <div className={className} onClick={(e) => e.stopPropagation()}>
        <InputComponent
          value={editValue}
          onChange={(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
            setEditValue(e.target.value)
          }
          placeholder={placeholder}
          className="mb-2"
          rows={multiline ? 3 : undefined}
          maxLength={maxLength}
          onKeyDown={(
            e: KeyboardEvent<HTMLInputElement | HTMLTextAreaElement>,
          ) => {
            if (e.key === 'Enter' && !multiline) {
              handleSave();
            }
            if (e.key === 'Escape') {
              handleCancel();
            }
          }}
        />
        <Stack gap={2} direction="horizontal">
          <Button size="sm" onClick={handleSave} disabled={isLoading}>
            <Icon name="Check" size="auto" />
            Save
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={handleCancel}
            disabled={isLoading}
          >
            <Icon name="X" size="auto" />
            Cancel
          </Button>
        </Stack>
      </div>
    );
  }

  return (
    <Stack className={className} gap={2} direction="horizontal">
      {children}
      {!isReadOnly && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              onClick={(e) => {
                e.stopPropagation();
                setIsEditing(true);
              }}
              aria-label={`Edit ${placeholder?.toLowerCase()}`}
            >
              <Icon name="Pencil" size="auto" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>{`Edit ${placeholder?.toLowerCase()}`}</TooltipContent>
        </Tooltip>
      )}
    </Stack>
  );
};
